/**
 * GUMSHOE — pure game-state transitions and case-graph queries.
 * No bus access, no side effects; index.ts owns the wiring. Owned by GAME.
 */
import type { ClueDef, GameState, SuspectDef } from '../core/types'
import { CLUES, CLUE_PREREQS, REAL_CLUE_IDS, SUSPECTS } from './data'

export const createInitialState = (): GameState => ({
  phase: 'title',
  zone: 'outdoor',
  collectedClueIds: [],
  journalOpen: false,
  accusedSuspectId: null
})

// ---------------------------------------------------------------------------
// Lookups
// ---------------------------------------------------------------------------

export const CLUE_ID_SET: ReadonlySet<string> = new Set(CLUES.map((c) => c.id))
export const SUSPECT_ID_SET: ReadonlySet<string> = new Set(SUSPECTS.map((s) => s.id))

export const findClue = (clueId: string): ClueDef | undefined =>
  CLUES.find((c) => c.id === clueId)

export const findSuspect = (suspectId: string): SuspectDef | undefined =>
  SUSPECTS.find((s) => s.id === suspectId)

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export const isCollected = (state: GameState, clueId: string): boolean =>
  state.collectedClueIds.includes(clueId)

/** True when every prerequisite in the clue dependency graph is collected. */
export const prereqsMet = (state: GameState, clueId: string): boolean =>
  (CLUE_PREREQS[clueId] ?? []).every((id) => isCollected(state, id))

export const hasRequiredClues = (state: GameState, suspect: SuspectDef): boolean =>
  suspect.requiredClueIds.every((id) => isCollected(state, id))

/** All six real clues in the case file — red herrings don't count. */
export const allRealCluesCollected = (state: GameState): boolean =>
  REAL_CLUE_IDS.every((id) => isCollected(state, id))

// ---------------------------------------------------------------------------
// Transitions (always return a new state object)
// ---------------------------------------------------------------------------

export const withClue = (state: GameState, clueId: string): GameState => ({
  ...state,
  collectedClueIds: [...state.collectedClueIds, clueId]
})

export const withAccusation = (
  state: GameState,
  suspectId: string,
  won: boolean
): GameState => ({
  ...state,
  phase: won ? 'won' : 'lost',
  accusedSuspectId: suspectId
})
